/**
 * Meta Cloud API — Facebook SDK Configuration
 *
 * Uses FB.login() popup (Embedded Signup) instead of redirect-based OAuth.
 * The config_id comes from Meta's WhatsApp Embedded Signup configuration.
 *
 * META_APP_ID and META_CONFIG_ID are defined per environment
 * in the vite.config.{local|dev|prod}.ts `define` blocks,
 * following the same pattern as URL_API_PLICK, FIREBASE_API_KEY, etc.
 *
 * Coexistence: el negocio conecta el número que YA usa en la app de WhatsApp
 * Business. Se activa pasando `extras.featureType = 'whatsapp_business_app_onboarding'`.
 * En ese flujo el popup reemplaza la selección de WABA por una pantalla de "conectar
 * cuenta existente" (código de verificación en la app) y el postMessage de éxito
 * (`event: FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING`) trae SOLO `waba_id` — el
 * `phone_number_id` lo resuelve el backend con GET /{waba_id}/phone_numbers.
 *
 * See: https://developers.facebook.com/documentation/business-messaging/whatsapp/embedded-signup/onboarding-business-app-users
 */

/* eslint-disable no-var */
declare const META_APP_ID: string;
declare const META_CONFIG_ID: string;
/* eslint-enable no-var */

const META_SDK_VERSION = 'v25.0';

/**
 * featureType que activa el flujo Coexistence del Embedded Signup.
 * Si se quisiera revertir al onboarding Cloud API estándar, basta con no pasar `extras`.
 */
export const COEXISTENCE_FEATURE_TYPE = 'whatsapp_business_app_onboarding';

/** Datos del comercio en sesión que se usan para precargar el diálogo de Meta. */
export interface EmbeddedSignupBusinessPrefill {
  name?: string;
  email?: string;
  website?: string;
  /** Tal como lo guarda el comercio: long_name de Google Places ('México'), no ISO-2. */
  country?: string;
}

/** Forma exacta que Meta espera en `extras.setup.business` (paridad con el Embedded Signup de Kapso). */
interface MetaSetupBusiness {
  name: string;
  email: string;
  website: string;
  address: { country: string };
}

/**
 * `address.country` del comercio se guarda como long_name de Google Places ('Mexico',
 * 'México') o con el default literal 'Mexico'; Meta espera ISO 3166-1 alpha-2 ('MX').
 * Lo que no se pueda mapear se manda como '' — Meta deja el campo vacío para que el
 * comercio lo elija, que es mejor que enviarle un valor que va a descartar igual.
 */
const COUNTRY_ISO2_BY_NAME: Record<string, string> = {
  mexico: 'MX',
  méxico: 'MX',
  'estados unidos': 'US',
  'united states': 'US',
  'estados unidos de américa': 'US',
  'united states of america': 'US',
};

export const toIso2Country = (country?: string): string => {
  const raw = (country || '').trim();
  if (!raw) return '';
  if (/^[a-z]{2}$/i.test(raw)) return raw.toUpperCase();
  return COUNTRY_ISO2_BY_NAME[raw.toLowerCase()] || '';
};

/**
 * Construye `extras.setup.business`, el bloque que precarga la pantalla "Completa la
 * información de tu empresa" del diálogo de Meta. Sin él el comercio teclea todo a mano.
 *
 * Todas las claves salen SIEMPRE presentes y como string: un `undefined` se pierde al
 * serializar el objeto hacia el popup y dejaría el bloque incompleto.
 *
 * @param {EmbeddedSignupBusinessPrefill} [business] Datos del comercio en sesión.
 * @returns {MetaSetupBusiness} Bloque listo para `extras.setup.business`.
 */
export const buildSetupBusiness = (business?: EmbeddedSignupBusinessPrefill): MetaSetupBusiness => ({
  name: business?.name?.trim() || '',
  email: business?.email?.trim() || '',
  website: business?.website?.trim() || '',
  address: { country: toIso2Country(business?.country) },
});

/** Resultado del Embedded Signup: el code más los identificadores capturados del postMessage. */
export interface EmbeddedSignupResult {
  code: string;
  wabaId?: string;
  phoneNumberId?: string;
  featureType: string;
  finishEvent?: string;
}

/** Estructura del `message` event que Meta emite durante el Embedded Signup. */
interface WaEmbeddedSignupMessage {
  type: 'WA_EMBEDDED_SIGNUP';
  event?: string;
  version?: number;
  data?: {
    waba_id?: string;
    phone_number_id?: string;
    business_id?: string;
    current_step?: string;
    error_message?: string;
  };
}

/**
 * Load the Facebook SDK asynchronously.
 * Idempotent: if already loaded, resolves immediately.
 * @returns {Promise<void>} Resolves when SDK is ready
 */
export const loadFacebookSdk = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Already loaded
    if (window.FB) {
      resolve();
      return;
    }

    // Init callback
    window.fbAsyncInit = function () {
      window.FB.init({
        appId: META_APP_ID,
        cookie: true,
        xfbml: false,
        version: META_SDK_VERSION,
      });
      resolve();
    };

    // Inject SDK script if not already present
    if (!document.getElementById('facebook-jssdk')) {
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      script.onerror = () => reject(new Error('Failed to load Facebook SDK'));
      document.head.appendChild(script);
    }
  });
};

/**
 * Launch the Meta Embedded Signup popup via FB.login() en modo Coexistence.
 *
 * Captura el `waba_id` (y `phone_number_id` si el flujo lo provee) vía el
 * `message` event de Meta y lo combina con el auth code del callback de FB.login.
 *
 * @param {EmbeddedSignupBusinessPrefill} [business] Datos del comercio en sesión que precargan el
 *   formulario de Meta. Opcional: sin él el bloque viaja igual pero con strings vacíos.
 * @returns {Promise<EmbeddedSignupResult | null>} Resultado o null si el usuario cancela.
 */
export const launchEmbeddedSignup = (
  business?: EmbeddedSignupBusinessPrefill,
): Promise<EmbeddedSignupResult | null> => {
  return new Promise((resolve, reject) => {
    if (!window.FB) {
      reject(new Error('Facebook SDK not loaded'));
      return;
    }

    // El postMessage de Coexistence llega ANTES del callback de FB.login y trae solo
    // `waba_id`; lo guardamos para fusionarlo con el `code` cuando resuelva el login.
    let sessionInfo: { wabaId?: string; phoneNumberId?: string; finishEvent?: string } = {};

    // Validar el origen por hostname exacto, no por sufijo: `endsWith('facebook.com')`
    // dejaría pasar `https://evilfacebook.com`. Aceptamos facebook.com y sus subdominios.
    const isFacebookOrigin = (origin: string): boolean => {
      try {
        const { protocol, hostname } = new URL(origin);
        return protocol === 'https:' && (hostname === 'facebook.com' || hostname.endsWith('.facebook.com'));
      } catch {
        return false;
      }
    };

    const sessionInfoListener = (event: MessageEvent) => {
      if (!isFacebookOrigin(event.origin)) return;
      try {
        const message = JSON.parse(event.data) as WaEmbeddedSignupMessage;
        if (message.type !== 'WA_EMBEDDED_SIGNUP') return;
        sessionInfo = {
          wabaId: message.data?.waba_id,
          phoneNumberId: message.data?.phone_number_id,
          finishEvent: message.event,
        };
      } catch {
        // Mensaje no-JSON o ajeno al Embedded Signup — ignorar.
      }
    };

    window.addEventListener('message', sessionInfoListener);
    const cleanup = () => window.removeEventListener('message', sessionInfoListener);

    // Igualado al Embedded Signup de Kapso (Tech Provider que sí lanza coexistence):
    // `featureType` va emparejado con `sessionInfoVersion:'3'` + `display:'popup'`.
    // Sin `sessionInfoVersion` Meta puede ignorar el featureType y caer al flujo estándar.
    const loginOptions = {
      config_id: META_CONFIG_ID,
      response_type: 'code',
      override_default_response_type: true,
      display: 'popup',
      extras: {
        // `setup.business` precarga la pantalla "Completa la información de tu empresa".
        // Sin este bloque el comercio teclea nombre, correo, sitio y país a mano.
        setup: { business: buildSetupBusiness(business) },
        sessionInfoVersion: '3',
        featureType: COEXISTENCE_FEATURE_TYPE,
      },
    };

    window.FB.login((response: { authResponse?: { code?: string } }) => {
      cleanup();
      const code = response.authResponse?.code;
      if (code) {
        resolve({
          code,
          wabaId: sessionInfo.wabaId,
          phoneNumberId: sessionInfo.phoneNumberId,
          featureType: COEXISTENCE_FEATURE_TYPE,
          finishEvent: sessionInfo.finishEvent,
        });
      } else {
        // User cancelled or closed the popup
        resolve(null);
      }
    }, loginOptions);
  });
};

// Extend window for Facebook SDK types
declare global {
  interface Window {
    FB: {
      init: (params: { appId: string; cookie: boolean; xfbml: boolean; version: string }) => void;
      login: (
        callback: (response: { authResponse?: { code?: string } }) => void,
        options: {
          config_id: string;
          response_type: string;
          override_default_response_type: boolean;
          display?: string;
          extras?: {
            setup?: { business?: MetaSetupBusiness } & Record<string, unknown>;
            featureType?: string;
            sessionInfoVersion?: string;
          };
        },
      ) => void;
    };
    fbAsyncInit: () => void;
  }
}
